import { LiteGraph } from "litegraph.js";

const H = LiteGraph.NODE_WIDGET_HEIGHT; // 20
const GAP = 4;
const ROW = H + GAP; // 24
const OFFSET = LiteGraph.NODE_SLOT_HEIGHT * 0.5; // 10

/**
 * Adds a slider widget with an integrated input connection point.
 * The input dot appears on the left edge of the slider itself.
 * When a value is connected, the slider animates to show the incoming value.
 *
 * Call finalizeDrivableSliders() after all sliders are added to set positions.
 */
export function addDrivableSlider(node, label, propKey, initial, min, max, step = 1, onChange) {
  const widget = node.addWidget("slider", label, initial, (val) => {
    node.properties[propKey] = val;
    if (onChange) onChange(val);
  }, { min, max, step });

  const widgetIdx = node.widgets.length - 1;

  // Add hidden input — empty name so no label is rendered
  node.addInput("", "number");
  const inputIdx = node.inputs.length - 1;

  if (!node._drivable) node._drivable = [];
  node._drivable.push({ inputIdx, widgetIdx, propKey, widget });

  return widget;
}

/**
 * Call after all widgets and inputs are added.
 * Sets widgets_start_y and positions each drivable input dot at its widget.
 *
 * @param {LGraphNode} node
 * @param {number} numVisibleInputs – number of regular (non-drivable) inputs
 */
export function finalizeDrivableSliders(node, numVisibleInputs) {
  node.widgets_start_y = numVisibleInputs * LiteGraph.NODE_SLOT_HEIGHT;

  if (!node._drivable) return;

  for (const d of node._drivable) {
    const widgetCenterY = node.widgets_start_y + 2 + d.widgetIdx * ROW + H * 0.5;
    node.inputs[d.inputIdx].pos = [OFFSET, widgetCenterY];
  }
}

/**
 * Call in onExecute() to sync connected input values into widget sliders.
 * When an oscillator (or any source) is connected, the slider animates.
 */
export function syncDrivableSliders(node) {
  if (!node._drivable) return;

  for (const { inputIdx, widget, propKey } of node._drivable) {
    if (node.isInputConnected(inputIdx)) {
      const val = node.getInputData(inputIdx);
      if (val != null) {
        node.properties[propKey] = val;
        widget.value = val;
      }
    }
  }
}
